/**
 * Cross-tab progress + status sync for chunked uploads.
 *
 * Problem: SSE-based sync (per PATCH-chunk boundary on the backend) delivered progress
 * updates to duplicated tabs with a ~5 s lag because chunks are ~10 MB. Even worse,
 * terminal states like COMPLETED never crossed at all — the backend's SseEmitter only
 * closes for PUBLISHED/REJECTED/FAILED/CANCELLED, and my previous handler only forwarded
 * bytes-and-files, never status.
 *
 * Solution: the owning tab broadcasts every updateUploadingPackage payload over a
 * BroadcastChannel; sibling tabs listen and re-dispatch locally. Same event stream, no
 * network hop, no chunk-boundary lag. Terminal transitions cross the wire alongside
 * progress ticks, so a completed upload flips both tabs to COMPLETED simultaneously.
 *
 * Echo protection: broadcasts only fire when the tab actually owns the upload (checked
 * via isOwnedByThisTab), and receivers set a module-level `receiving` flag so their
 * own re-dispatch is not re-broadcast. Together they prevent A→B→A loops.
 */

import { isOwnedByThisTab } from "./uploadOwnership";
import { getCurrentOwnerUid } from "./currentOwnerUid";

const CHANNEL_NAME = "clipn-upload-cross-tab-v1";

let channel = null;
let receiving = false;

const getChannel = () => {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch (_) {
      return null;
    }
  }
  return channel;
};

// Multi-tenancy scoping (2026-08-05 leak fix). Two sessions of different
// users in the same browser share this BroadcastChannel; without the uid
// stamp+filter, admin's tab could re-dispatch a status change into manager's
// Redux the moment their tabs overlap in time. Missing-ownerUid messages
// from older bundles are dropped defensively rather than adopted.
export const isReceivingCrossTabBroadcast = () => receiving;

export const broadcastUploadUpdate = (payload) => {
  if (receiving) return;
  if (!payload || typeof payload !== "object") return;
  if (!isOwnedByThisTab(payload.uploadId)) return;
  const ownerUid = getCurrentOwnerUid();
  if (!ownerUid) return;
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: "upload-update", ownerUid, payload });
  } catch (_) {
    /* channel closed / unsupported — silently drop */
  }
};

export const subscribeToUploadUpdates = (handler) => {
  const ch = getChannel();
  if (!ch || typeof handler !== "function") return () => {};
  const listener = (evt) => {
    if (evt?.data?.type !== "upload-update") return;
    const currentUid = getCurrentOwnerUid();
    if (!currentUid) return;
    if (evt.data.ownerUid !== currentUid) return;
    receiving = true;
    try {
      handler(evt.data.payload);
    } finally {
      receiving = false;
    }
  };
  ch.addEventListener("message", listener);
  return () => {
    try { ch.removeEventListener("message", listener); } catch (_) { /* ignore */ }
  };
};
